import { useTranslation } from 'react-i18next';
import { useHealth } from '@/entities/distribution';
import { useZohoConfig } from '@/entities/zoho';
import { APP_ENV } from '@/shared/lib';
import { Badge } from '@/shared/ui';

/** The build's environment (DEVELOPMENT / STAGING / PRODUCTION), as the old top bar's tag. */
export function EnvBadge() {
  const { t, i18n } = useTranslation();
  const key = `env.${APP_ENV}`;
  return (
    <Badge size="sm" data-status={APP_ENV}>
      {i18n.exists(key) ? t(key) : APP_ENV}
    </Badge>
  );
}

/** The public health check, as the old top bar read it: API state, plus the sync worker when it is not up. */
export function ApiStatus() {
  const { t } = useTranslation();
  const health = useHealth();
  const body = health.data;
  if (health.isPending) return <Badge size="sm">{t('status.api')}: …</Badge>;
  // A 5xx without the health shape (proxy or API error) is "unreachable", never a crash of the shell.
  if (!body?.checks) {
    const down = `${t('status.api')}: ${t('status.apiDown')}`;
    return (
      <Badge tone="fail" size="sm" dot title={down} data-status="DOWN">
        <span className="app-top__pill-text">{down}</span>
      </Badge>
    );
  }
  const label =
    `${t('status.api')}: ${body.status === 'ok' ? t('status.apiOk') : t('status.apiDegraded')}` +
    (body.checks.queue !== 'up' ? ` · ${t('status.queue')}: ${t(`status.${body.checks.queue}`)}` : '');
  const details = `${t('status.database')}: ${t(`status.${body.checks.database}`)} · ${t('status.queue')}: ${t(`status.${body.checks.queue}`)}`;
  return (
    // The words can fold away on a narrow bar (UX audit #9); the tooltip always carries them.
    <Badge tone={body.status === 'ok' ? 'ok' : 'partial'} size="sm" dot title={`${label}\n${details}`} data-status={body.status}>
      <span className="app-top__pill-text">{label}</span>
    </Badge>
  );
}

/** The stored Zoho Desk state (manageZoho only: the config is that permission's endpoint). */
export function ZohoStatus() {
  const { t } = useTranslation();
  const config = useZohoConfig();
  if (!config.data) return null;
  const c = config.data;
  const [tone, label] = !c.configured
    ? (['partial', t('status.zohoNotConfigured')] as const)
    : c.lastTestOk === false
      ? (['fail', t('status.zohoTestFailed')] as const)
      : c.lastTestOk === null
        ? (['neutral', t('status.zohoUntested')] as const)
        : (['ok', t('status.zohoConnected')] as const);
  const text = `${t('status.zoho')}: ${label}`;
  return (
    <Badge tone={tone} size="sm" dot title={text} data-status={c.configured ? 'CONFIGURED' : 'NOT_CONFIGURED'}>
      <span className="app-top__pill-text">{text}</span>
    </Badge>
  );
}
